import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import SimpleImageLoader from './SimpleImageLoader';
import { fetchPhotoById } from '../services/api';
import { getImageUrls, hasValidPhotoName } from '../utils/imageUtils';
import './PhotoDetail.css';

const PhotoDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get the previous search params to preserve filter state
  const previousSearch = location.state?.from || '';

  // Parse filter information from URL parameters
  const getActiveFilter = () => {
    if (!previousSearch) return null;
    
    const searchParams = new URLSearchParams(previousSearch);
    const filterType = searchParams.get('filterType');
    const filterValue = searchParams.get('filterValue');
    
    if (filterType && filterValue) {
      const filterTypeLabels = {
        airport: 'Airport',
        operator: 'Operator',
        aircraftType: 'Aircraft Type',
        registration: 'Registration',
        category: 'Category'
      };
      
      return {
        type: filterType,
        value: decodeURIComponent(filterValue),
        displayLabel: `${filterTypeLabels[filterType] || filterType}: ${decodeURIComponent(filterValue)}`
      };
    }
    
    return null;
  };

  const activeFilter = getActiveFilter();

  // Helper function to create filter URLs
  const createFilterUrl = (filterType, filterValue) => {
    if (!filterValue || filterValue === 'N/A' || filterValue === 'Unknown') {
      return null;
    }
    
    const params = new URLSearchParams();
    params.set('filterType', filterType);
    params.set('filterValue', encodeURIComponent(filterValue));
    return `/?${params.toString()}`;
  };

  // Helper function to render clickable metadata value
  const renderMetadataValue = (value, filterType, defaultText = 'N/A') => {
    const displayValue = value || defaultText;
    const filterUrl = createFilterUrl(filterType, value);
    
    if (filterUrl && value && value !== defaultText) {
      return (
        <Link to={filterUrl} className="metadata-value-link">
          {displayValue}
        </Link>
      );
    }
    
    return <span className="metadata-value">{displayValue}</span>;
  };

  useEffect(() => {
    loadPhoto();
  }, [id]);

  const loadPhoto = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPhotoById(id);
      setPhoto(data.photo);
    } catch (err) {
      setError('Failed to load photo details. Please try again.');
    } finally {
      setLoading(false);
    }
  };



  if (loading) return <LoadingSpinner />;
  if (error) return <div className="error-message">{error}</div>;
  if (!photo) return <div className="error-message">Photo not found</div>;

  const renderImage = () => {
    if (!hasValidPhotoName(photo)) {
      return (
        <div className="photo-detail-image-error">
          <div className="error-icon">📷</div>
          <p>Image not available</p>
          <p className="error-details">
            {photo.aircraftType || 'Aircraft'} - {photo.registration || 'Unknown'}
          </p>
        </div>
      );
    }

    return (
      <div className="photo-detail-image-wrapper">
        <SimpleImageLoader
          photo={photo}
          size="large"
          className="photo-detail-image"
          height="400px"
        />
      </div>
    );
  };

  return (
    <div className="photo-detail">
      <div className="photo-detail-header">
        <Link to={`/${previousSearch}`} className="back-button">
          ← Back to Gallery
        </Link>
        {activeFilter && (
          <div className="filter-status">
            <span className="filter-status-label">Filtered by:</span>
            <span className="filter-status-value">{activeFilter.displayLabel}</span>
          </div>
        )}
      </div>
      
      <div className="photo-detail-content">
        <div className="photo-detail-image-container">
          {renderImage()}
        </div>
        
        <div className="photo-detail-info">
          <h1 className="photo-title">
            {photo.aircraftType || 'Unknown Aircraft'}
          </h1>
          
          <div className="photo-metadata">
            <div className="metadata-row">
              <span className="metadata-label">Registration:</span>
              {renderMetadataValue(photo.registration, 'registration', 'N/A')}
            </div>
            
            <div className="metadata-row">
              <span className="metadata-label">Operator:</span>
              {renderMetadataValue(photo.operator, 'operator', 'Unknown')}
            </div>
            
            <div className="metadata-row">
              <span className="metadata-label">Airport:</span>
              {renderMetadataValue(photo.iata, 'airport', 'Unknown')}
            </div>
            
            {photo.photoCategory && (
              <div className="metadata-row">
                <span className="metadata-label">Category:</span>
                {renderMetadataValue(photo.photoCategory, 'category')}
              </div>
            )}
            
            {photo.photoDate && (
              <div className="metadata-row">
                <span className="metadata-label">Date:</span>
                <span className="metadata-value">
                  {new Date(photo.photoDate).toLocaleDateString()}
                </span>
              </div>
            )}
            
            {photo.photographer && (
              <div className="metadata-row">
                <span className="metadata-label">Photographer:</span>
                <span className="metadata-value">{photo.photographer}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoDetail;